// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Combobox,
    Option,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Label,
    makeStyles,
    tokens,
    ComboboxProps,
} from '@fluentui/react-components';
import { Checkmark20Filled, Dismiss12Regular } from '@fluentui/react-icons';
import React, { useCallback } from 'react';
import { useChat } from '../../../libs/hooks';
import { IGraphChatUser } from '../../../libs/models/GraphChatUser';
import { debounce } from 'lodash';

const useStyles = makeStyles({
    content: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalMNudge,
    },
    tagsList: {
        listStyleType: "none",
        marginBottom: tokens.spacingVerticalXXS,
        marginTop: 0,
        paddingLeft: 0,
        display: "flex",
        gridGap: tokens.spacingHorizontalXXS,
    },
});

interface InvitationCreateDialogProps {
    open: boolean;
    onCloseDialog: () => void;
    chatId: string;
}

/* eslint-disable */
const debounceSearchUsers = debounce((searchTerm: string, chat: any, setSearchResults: (results: IGraphChatUser[]) => void) => {
    chat.searchUsers(searchTerm).then((results: IGraphChatUser[]) => {
        setSearchResults(results);
    }).catch((error: any) => {
        console.error('Error searching users:', error);
    });
}, 300);
/* eslint-enable */

export const InvitationCreateDialog: React.FC<InvitationCreateDialogProps> = ({open, onCloseDialog, chatId}) => {
    const [isIdCopied, setIsIdCopied] = React.useState<boolean>(false);
    const [searchResults, setSearchResults] = React.useState<IGraphChatUser[]>([]);
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [selectedUsers, setSelectedUsers] = React.useState<IGraphChatUser[]>([]);

    const classes = useStyles();
    const chat = useChat();

    const copyId = useCallback(() => {
        void navigator.clipboard.writeText(chatId).then(() => {
            setIsIdCopied(true);
        });
    }, [chatId]);

    const handleSearch = useCallback((searchTerm: string) => {
        setSearchTerm(searchTerm);
        if (searchTerm.trim()) {
            debounceSearchUsers(searchTerm.trim(), chat, setSearchResults);
        }
    }, [setSearchTerm, chat]);

    const handleInvite = () => {
        Promise.all(selectedUsers.map(user => chat.addUserToChat(chatId, user.id)))
            .then(() => {
                console.log('All users invited');
            })
            .catch((error) => {
                console.error('Error inviting users:', error);
            });
        onCloseDialog();
        setSelectedUsers([]);
        setSearchTerm('');
        setSearchResults([]);
    }

    const onSelect: ComboboxProps["onOptionSelect"] = (_event, data) => {
        const foundUser = searchResults.find((user) => user.id === data.optionValue);
        if (foundUser) {
            if (selectedUsers.some(user => user.id === foundUser.id)) {
                setSelectedUsers(selectedUsers.filter(user => user.id !== foundUser.id));
            } else {
                setSelectedUsers([...selectedUsers, foundUser]);
            }
            setSearchTerm('');
            setSearchResults([]);
        }
    }

    return (
        <div>
            <Dialog open={open}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Invite others to your Bot</DialogTitle>
                        <DialogContent className={classes.content}>
                            <Label>Please provide the following Chat ID to your colleagues so they can join the chat.</Label>
                            <Label data-testid="invitationDialogChatIDLabel" weight="semibold">
                                {chatId}
                            </Label>
                            <Label>Or search for and select users below to invite to your chat.</Label>
                            {selectedUsers.length > 0 && (
                                <Label>Selected users:</Label>
                            )}
                            {selectedUsers.map((user) => (
                                <li key={user.id} className={classes.tagsList}>
                                    <Button
                                        size={"small"}
                                        appearance="primary"
                                        shape={"circular"}
                                        icon={<Dismiss12Regular/>}
                                        iconPosition="after"
                                        onClick={() => {
                                            setSelectedUsers(selectedUsers.filter(u => u.id !== user.id))
                                        }}
                                    >
                                        {user.name}
                                    </Button>
                                </li>
                            ))}
                            <Combobox
                                placeholder="Search for users to invite"
                                onInput={(event) => {
                                    handleSearch(event.currentTarget.value)
                                }}
                                value={searchTerm}
                                onOptionSelect={onSelect}
                                onKeyDown={(event) => {
                                    if (event.key === ' ') {
                                        event.preventDefault();
                                        handleSearch(searchTerm + ' ')
                                    }
                                }}
                            >
                                {searchResults.map((user) => (
                                    <Option key={user.id} value={user.id}>
                                        {user.name}
                                    </Option>
                                ))}
                            </Combobox>
                        </DialogContent>
                        <DialogActions>
                            <Button data-testid="invitationDialogCloseButton" appearance="secondary" onClick={onCloseDialog}>
                                Close
                            </Button>
                            <Button
                                data-testid="invitationDialogChatIDCopyButton"
                                appearance="primary"
                                onClick={copyId}
                                icon={isIdCopied ? <Checkmark20Filled/> : null}
                            >
                                {isIdCopied ? 'Copied' : 'Copy'}
                            </Button>
                            <Button appearance="primary" onClick={handleInvite}>
                                Invite
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};
